import type { Lead } from '@/types/leads';
import type {
  LeadOutreachEvent,
  OutreachEmailDraft,
  OutreachStage,
  OutreachTemplateKey,
  OutreachTrack,
} from '@/types/lead-outreach';
import { buildEmailBody, buildEmailSubject } from '@/lib/outreach/email-copy';
import {
  getIncumbentVendorLabel,
  getPrimaryPainId,
  getPainAngleLabel,
  hasCompetitorIncumbent,
  suggestPinchHitTemplateFromPains,
} from '@/lib/outreach/pain-routing';

/** Cleared anonymized proof links — safe for cold outreach (docs/13-OUTREACH-PACK). */
export const CLEARED_PROOF_URLS = {
  basket: 'https://admin.cannabis-kiosks.com/retailer-basket-lift-brief-april-2026.html',
  brandSpotlight: 'https://admin.cannabis-kiosks.com/store-c-spotlight-case-study-may-2026.html',
} as const;

function storeName(lead: Lead): string {
  return lead.dba?.trim() || lead.business_name?.trim() || 'your store';
}

function firstName(lead: Lead): string {
  const person = lead.contact_person?.trim();
  if (!person) return 'there';
  return person.split(/\s+/)[0] || 'there';
}

export function suggestPinchHitTemplate(
  lead: Lead,
  events: LeadOutreachEvent[] = []
): OutreachTemplateKey {
  const lastSent = events.find(
    (event) => event.event_type === 'sent' || event.event_type === 'follow_up_sent'
  );
  if (lastSent) {
    const replied = events.some((event) => event.event_type === 'replied');
    if (!replied) {
      const lastTemplate = lastSent.template_key as OutreachTemplateKey | undefined;
      const sentAt = lastSent.occurred_at ? new Date(lastSent.occurred_at).getTime() : 0;
      const daysSince = (Date.now() - sentAt) / (1000 * 60 * 60 * 24);
      if (lastTemplate === 'follow_up_2') return 'follow_up_2';
      if (lastTemplate === 'follow_up') return daysSince >= 5 ? 'follow_up_2' : 'follow_up';
      if (lastTemplate && daysSince >= 3) return 'follow_up';
    }
  }
  return suggestPinchHitTemplateFromPains(lead);
}

export { getPrimaryPainId, getPainAngleLabel };

/** Reference link for admin UI and sent-event logging — not inserted into cold email bodies. */
export function proofUrlForTemplate(templateKey: OutreachTemplateKey): string | null {
  if (templateKey === 'A2_brand' || templateKey === 'B3_proposal_followup') {
    return CLEARED_PROOF_URLS.brandSpotlight;
  }
  if (templateKey === 'follow_up' || templateKey === 'follow_up_2') {
    return null;
  }
  if (templateKey.startsWith('A1_') || templateKey.startsWith('B')) {
    return CLEARED_PROOF_URLS.basket;
  }
  return null;
}

export function suggestNurtureTemplate(
  lead: Lead,
  events: LeadOutreachEvent[]
): OutreachTemplateKey {
  const lastSent = events.find((event) => event.event_type === 'sent' || event.event_type === 'follow_up_sent');
  const lastTemplate = lastSent?.template_key as OutreachTemplateKey | undefined;
  const replied = events.some((event) => event.event_type === 'replied');
  const sentAt = lastSent?.occurred_at ? new Date(lastSent.occurred_at).getTime() : 0;
  const daysSince = lastSent ? (Date.now() - sentAt) / (1000 * 60 * 60 * 24) : 0;

  if (lastSent && !replied) {
    if (lastTemplate === 'follow_up_2') return 'follow_up_2';
    if (lastTemplate === 'follow_up') return daysSince >= 5 ? 'follow_up_2' : 'follow_up';
    if (lastTemplate && lastTemplate.startsWith('B') && daysSince >= 3) return 'follow_up';
  }

  const notes = `${lead.notes ?? ''} ${lead.qa_notes ?? ''}`.toLowerCase();
  if (notes.includes('proposal') || notes.includes('quoted')) return 'B3_proposal_followup';
  if (notes.includes('full rollout') || notes.includes('whole store') || notes.includes('not ready')) {
    return 'B2_one_station';
  }

  const month = new Date().getMonth();
  if (month === 3 || month === 10) return 'B4_peak_day';

  return 'B1_timing';
}

export function suggestTrack(lead: Lead, events: LeadOutreachEvent[]): OutreachTrack {
  const latest = events[0];
  if (latest) return latest.track;
  const notes = `${lead.notes ?? ''} ${lead.qa_notes ?? ''}`.toLowerCase();
  if (
    notes.includes('talked before') ||
    notes.includes('not now') ||
    notes.includes('follow up') ||
    notes.includes('proposal') ||
    notes.includes('closed_lost')
  ) {
    return 'nurture';
  }
  return 'pinch_hit';
}

export function suggestTemplate(
  lead: Lead,
  track: OutreachTrack,
  events: LeadOutreachEvent[]
): OutreachTemplateKey {
  if (track === 'nurture') return suggestNurtureTemplate(lead, events);
  return suggestPinchHitTemplate(lead, events);
}

function buildSubject(templateKey: OutreachTemplateKey, lead: Lead): string {
  return buildEmailSubject(templateKey, storeName(lead));
}

function buildBody(templateKey: OutreachTemplateKey, lead: Lead, proofUrl: string | null): string {
  return buildEmailBody(templateKey, {
    firstName: firstName(lead),
    storeName: storeName(lead),
    posVendor: lead.pos_vendor,
    proofUrl,
    incumbentVendor: getIncumbentVendorLabel(lead),
    hasCompetitorIncumbent: hasCompetitorIncumbent(lead),
  });
}

function buildMailtoHref(email: string | null, subject: string, body: string): string | null {
  if (!email?.trim()) return null;
  const params = new URLSearchParams({
    subject,
    body,
  });
  return `mailto:${encodeURIComponent(email.trim())}?${params.toString()}`;
}

export function buildOutreachDraft(
  lead: Lead,
  track: OutreachTrack,
  templateKey: OutreachTemplateKey
): OutreachEmailDraft {
  const proofUrl = proofUrlForTemplate(templateKey);
  const subject = buildSubject(templateKey, lead);
  const body = buildBody(templateKey, lead, proofUrl);
  return {
    track,
    template_key: templateKey,
    subject,
    body,
    proof_url: proofUrl,
    mailto_href: buildMailtoHref(lead.email, subject, body),
  };
}

export function computeOutreachStage(
  events: LeadOutreachEvent[],
  queuedLeadIds: Set<string>,
  leadId: string
): OutreachStage {
  if (events.some((event) => event.event_type === 'opted_out')) return 'opted_out';
  if (events.some((event) => event.event_type === 'replied')) return 'replied';

  const touchEvents = events.filter((event) =>
    ['sent', 'follow_up_sent'].includes(event.event_type)
  );
  const latestTouch = touchEvents[0];
  // A due follow-up is cleared by any later send / follow-up send (not only by a reply).
  const pendingFollowUp = events.find((event) => {
    if (!event.follow_up_at) return false;
    if (new Date(event.follow_up_at).getTime() > Date.now()) return false;
    const clearedByLaterTouch = events.some(
      (later) =>
        ['sent', 'follow_up_sent', 'replied'].includes(later.event_type) &&
        new Date(later.occurred_at).getTime() > new Date(event.occurred_at).getTime()
    );
    return !clearedByLaterTouch;
  });

  if (pendingFollowUp && latestTouch) return 'follow_up_due';
  if (latestTouch) return 'sent';
  if (queuedLeadIds.has(leadId)) {
    return 'queued';
  }
  return 'not_contacted';
}

export function defaultFollowUpAt(days = 4): string {
  return defaultFollowUpAtFrom(new Date().toISOString(), days);
}

export function defaultFollowUpAtFrom(occurredAtIso: string, days = 4): string {
  const date = new Date(occurredAtIso);
  date.setDate(date.getDate() + days);
  date.setHours(9, 0, 0, 0);
  return date.toISOString();
}

export const PINCH_HIT_TEMPLATES: OutreachTemplateKey[] = [
  'A1_basket',
  'A1_menu',
  'A1_throughput',
  'A1_experience',
  'A1_loyalty',
  'A1_fulfillment',
  'A1_insight',
  'A2_brand',
  'follow_up',
  'follow_up_2',
];
export const NURTURE_TEMPLATES: OutreachTemplateKey[] = [
  'B1_timing',
  'B2_one_station',
  'B3_proposal_followup',
  'B4_peak_day',
  'follow_up',
  'follow_up_2',
];
